import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, MapPin, Phone, Upload, X, LogOut } from 'lucide-react'
import { Modal, Button, Input } from '../ui'
import { useAuthStore } from '../../stores/authStore'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import type { ShippingAddress } from '../../types/user'

interface ProfileCompletionModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

const profileCompletionSchema = z.object({
  firstName: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  lastName: z.string().min(2, 'Los apellidos deben tener al menos 2 caracteres'),
  addressLine1: z.string().min(5, 'La calle debe tener al menos 5 caracteres'),
  number: z.string().min(1, 'El número es obligatorio'),
  addressLine2: z.string().min(1, 'La información adicional es obligatoria'),
  city: z.string().min(2, 'La ciudad debe tener al menos 2 caracteres'),
  state: z.string().min(2, 'La provincia debe tener al menos 2 caracteres'),
  postalCode: z.string().regex(/^\d{5}$/, 'El código postal debe tener 5 dígitos'),
  country: z.string().default('España'),
  phone: z.string().min(1, 'El número de teléfono es obligatorio'),
})

type ProfileCompletionForm = z.infer<typeof profileCompletionSchema>

const ProfileCompletionModal = ({ isOpen, onClose, onComplete }: ProfileCompletionModalProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const { user, setUser } = useAuthStore()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm<ProfileCompletionForm>({
    resolver: zodResolver(profileCompletionSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      addressLine1: user?.defaultShippingAddress?.addressLine1 || '',
      number: user?.defaultShippingAddress?.number || '',
      addressLine2: user?.defaultShippingAddress?.addressLine2 || '',
      city: user?.defaultShippingAddress?.city || '',
      state: user?.defaultShippingAddress?.state || '',
      postalCode: user?.defaultShippingAddress?.postalCode || '',
      country: 'España',
      phone: user?.phoneNumber || ''
    }
  })

  // Update form values when user changes (important for phone number)
  useEffect(() => {
    if (user?.phoneNumber) {
      setValue('phone', user.phoneNumber)
    }
  }, [user?.phoneNumber, setValue])

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona un archivo de imagen válido')
        return
      }
      
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert('La imagen es demasiado grande. Máximo 2MB.')
        return
      }
      
      setPhotoFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64Result = e.target?.result as string
        setPhotoPreview(base64Result)
        
        // Update user immediately for real-time preview
        if (user) {
          const updatedUser = {
            ...user,
            photoURL: base64Result
          }
          setUser(updatedUser)
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const removePhoto = () => {
    setPhotoFile(null)
    setPhotoPreview(null)
    
    // Restore original photo immediately
    if (user) {
      const updatedUser = {
        ...user,
        photoURL: user.photoURL?.startsWith('data:') ? '' : user.photoURL || ''
      }
      setUser(updatedUser)
    }
  }

  const onSubmit = async (data: ProfileCompletionForm) => {
    if (!user) return

    setIsLoading(true)
    try {
      // Create shipping address
      const shippingAddress: ShippingAddress = {
        firstName: data.firstName,
        lastName: data.lastName,
        addressLine1: data.addressLine1,
        number: data.number,
        addressLine2: data.addressLine2,
        city: data.city,
        state: data.state,
        postalCode: data.postalCode,
        country: data.country,
        phone: data.phone,
        isDefault: true
      }

      // Update user in Firestore
      const userDoc = doc(db, 'users', user.id)
      const updateData = {
        firstName: data.firstName,
        lastName: data.lastName,
        displayName: `${data.firstName} ${data.lastName}`,
        phoneNumber: data.phone || user.phoneNumber,
        defaultShippingAddress: shippingAddress,
        shippingAddresses: [shippingAddress],
        profileComplete: true,
        profileCompletedAt: new Date()
      }

      // Save photo as base64 if provided
      if (photoPreview) {
        updateData.photoURL = photoPreview // Base64 data URL
      }

      await updateDoc(userDoc, updateData)

      // Update local user state
      const updatedUser = {
        ...user,
        ...updateData,
        shippingAddresses: [shippingAddress],
        defaultShippingAddress: shippingAddress
      }
      setUser(updatedUser)

      onComplete()
      reset()
      setPhotoFile(null)
      setPhotoPreview(null)
    } catch (error) {
      console.error('Error updating profile:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      showCloseButton={false}
      closeOnBackdropClick={false}
      closeOnEscape={false}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            ¡Bienvenido a WildDraw!
          </h2>
          <p className="text-gray-600 mt-2">
            Para poder participar en sorteos y recibir tus premios, <strong>es obligatorio</strong> completar tu perfil.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Profile Photo */}
          <div className="text-center">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Foto de perfil (opcional)
            </label>
            
            {photoPreview || user?.photoURL ? (
              <div className="relative inline-block">
                <img
                  src={photoPreview || user?.photoURL}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                />
                {photoPreview && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300">
                <Upload className="w-8 h-8 text-gray-400" />
              </div>
            )}
            
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
              id="photo-upload"
            />
            <label
              htmlFor="photo-upload"
              className="inline-block mt-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors"
            >
              {photoPreview ? 'Cambiar foto' : 'Subir foto'}
            </label>
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre"
              placeholder="Tu nombre"
              {...register('firstName')}
              error={errors.firstName?.message}
              leftIcon={<User className="w-5 h-5" />}
            />
            <Input
              label="Apellidos"
              placeholder="Tus apellidos"
              {...register('lastName')}
              error={errors.lastName?.message}
              leftIcon={<User className="w-5 h-5" />}
            />
          </div>

          <Input
            label="Teléfono"
            placeholder="+34 123 456 789"
            type="tel"
            {...register('phone')}
            error={errors.phone?.message}
            leftIcon={<Phone className="w-5 h-5" />}
            disabled={!!user?.phoneNumber}
            helperText={user?.phoneNumber ? 'Número verificado durante el inicio de sesión' : undefined}
          />

          {/* Shipping Address */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Dirección de envío
            </h3>
            <p className="text-blue-800 text-sm mb-4">
              Los premios se envían directamente a tu domicilio
            </p>

            <div className="space-y-4">
              <Input
                label="Calle / Avenida"
                placeholder="Calle de la Paz, Avenida de la Libertad..."
                {...register('addressLine1')}
                error={errors.addressLine1?.message}
              />

              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Número"
                  placeholder="123"
                  {...register('number')}
                  error={errors.number?.message}
                />
                <div className="col-span-2">
                  <Input
                    label="Información adicional"
                    placeholder="Piso 3, Puerta A, Portal 2..."
                    {...register('addressLine2')}
                    error={errors.addressLine2?.message}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Ciudad"
                  placeholder="Tu ciudad"
                  {...register('city')}
                  error={errors.city?.message}
                />
                <Input
                  label="Provincia"
                  placeholder="Tu provincia"
                  {...register('state')}
                  error={errors.state?.message}
                />
                <Input
                  label="Código postal"
                  placeholder="12345"
                  {...register('postalCode')}
                  error={errors.postalCode?.message}
                />
              </div>

              <Input
                label="País"
                defaultValue="España"
                disabled
                {...register('country')}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={async () => {
                try {
                  const { signOut } = useAuthStore.getState()
                  await signOut()
                  onClose()
                } catch (error) {
                  console.error('Error signing out:', error)
                }
              }}
              className="order-2 sm:order-1"
              disabled={isLoading}
              leftIcon={<LogOut className="w-4 h-4" />}
            >
              Cerrar sesión
            </Button>
            <Button
              type="submit"
              className="order-1 sm:order-2 flex-1"
              isLoading={isLoading}
            >
              Completar perfil
            </Button>
          </div>
        </form>

        <div className="text-xs text-gray-500 text-center">
          Esta información es necesaria para participar en sorteos. Puedes modificarla después desde tu perfil.
        </div>
      </div>
    </Modal>
  )
}

export default ProfileCompletionModal